import axiosInstance from '../../api/axios.js';

export type ControlFileValidationError = {
  column?: string;
  reason?: string;
  value?: string;
};

export type ControlFileValidationRow = {
  row_number?: number;
  file_data_index?: number;
  passed?: boolean;
  errors?: ControlFileValidationError[];
};

export type ControlFileValidationResponse = {
  file_type?: string;
  file_data?: Array<Record<string, unknown>>;
  header_valid?: boolean;
  header_errors?: string[];
  total_rows?: number;
  passed_rows?: number;
  failed_rows?: number;
  error_rows?: ControlFileValidationRow[];
};

/** One row in POST `/api/control-upload/import` `file_data` (snake_case, matches validate). */
export type ControlBulkImportFileDataRow = Record<string, unknown>;

export type ControlBulkImportBody = {
  file_id?: string;
  file_data: ControlBulkImportFileDataRow[];
};

export type BulkImportControlsResult = {
  success?: boolean;
  created_controls?: Array<{
    control_id_was_deduped?: boolean;
    control_id?: string;
    requested_control_id?: string;
    id?: string;
    client_control_id?: string;
    title?: string;
  }>;
  message?: string;
};

export async function importControlsBulkUpload(
  body: ControlBulkImportBody
): Promise<BulkImportControlsResult> {
  const { data } = await axiosInstance.post<BulkImportControlsResult>('/api/control-upload/import', body);
  return data;
}

/** Bulk import on `/sox-controls/new` (control-upload import, not `/api/sox-controls/bulk-draft`). */
export async function bulkImportControlsDraft(
  body: ControlBulkImportBody
): Promise<BulkImportControlsResult> {
  return importControlsBulkUpload(body);
}

/** One control extracted by the AI from an uploaded file (any format/headers). */
export type ExtractedControl = {
  control_id?: string;
  control_title?: string;
  control_wording_raw?: string;
  control_objective?: string;
  frequency?: string;
  significance?: string;
  control_type?: string;
  sox_process?: string;
  control_performer?: string;
  row_number?: number;
};

/**
 * AI-extract controls from an uploaded spreadsheet/document via the
 * `/api/controls/extract-stream` SSE endpoint. Headers don't need to match a
 * template — the model reads the file and returns structured controls.
 */
export async function extractControlsStream(
  file: File,
  handlers: { onStatus?: (msg: string) => void } = {},
  signal?: AbortSignal
): Promise<ExtractedControl[]> {
  const form = new FormData();
  form.append('file', file);

  const response = await axiosInstance.post('/api/controls/extract-stream', form, {
    adapter: 'fetch',
    responseType: 'stream',
    validateStatus: () => true,
    signal,
  });

  if (response.status === 403) {
    let message = 'Extraction failed';
    try {
      const text = await new Response(response.data).text();
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || parsed.detail || message;
    } catch { /* keep default */ }
    throw new Error(message);
  }

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Extraction failed (status ${response.status})`);
  }

  const reader = (response.data as ReadableStream).getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'status';
  let controls: ExtractedControl[] = [];

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('event: ')) { currentEvent = line.slice(7).trim(); continue; }
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (currentEvent === 'error') throw new Error(data.message || 'Extraction error');
          if (currentEvent === 'status' && data.message) handlers.onStatus?.(data.message);
          if (currentEvent === 'result' && Array.isArray(data.controls)) controls = data.controls;
        } catch (e) {
          if (e instanceof Error && !e.message.includes('JSON')) throw e;
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* already released */ }
  }

  return controls;
}

export async function fetchSoxControl(controlId: string, auditYearId?: string | null): Promise<Record<string, unknown>> {
  const userHash = localStorage.getItem('userHash') || '';
  const { data } = await axiosInstance.get<Record<string, unknown>>(`/api/sox-controls/${controlId}`, {
    headers: { Authorization: userHash },
    params: auditYearId ? { audit_year_id: auditYearId } : undefined,
  });
  return data;
}
